'use client'

import React from 'react'
import { AnimateNumber } from 'motion-plus/react'

import DynamicIcon, { IconName } from '@/components/DynamicIcon'
import { RevealSection } from '@/components/RevealSection'
import { MembershipLevel, User } from '@/payload-types'
import { formatMoney } from '@/utilities/formatMoney'
import { cn } from '@/utilities/ui'

import { JoinButton } from './JoinButton'
import { MembershipColorScheme, MembershipColorSchemeKey, MembershipColorSchemes } from './themes'
import { JoinButtonIntent } from './types'

const TierCard: React.FC<{
  tier: MembershipLevel
  user: User | null
  userMemberLevel: MembershipLevel | null
  index: number
  colorScheme: MembershipColorScheme
  onJoinClick: (chosenPlan: MembershipLevel, intent: JoinButtonIntent) => Promise<void>
}> = ({ tier, user, userMemberLevel, colorScheme, onJoinClick }) => {
  return (
    <div
      className={cn(
        'flex w-full min-w-0 flex-col justify-between rounded-xl border-2 p-7 shadow-lg transition-transform duration-300 hover:translate-y-[-5px]',
        tier.mostPopular ? 'border-tuscany-400' : 'border-transparent',
        colorScheme.bg,
      )}
    >
      {tier.mostPopular && (
        <div className="absolute top-0 left-1/2 w-max -translate-1/2 rounded-xl bg-tuscany-400 p-1 px-4">
          <p className="text-sm text-evergreen-950">MOST POPULAR</p>
        </div>
      )}
      <h2 className={cn('font-display text-2xl font-bold', colorScheme.text)}>{tier.title}</h2>
      {tier.description && (
        <p className={cn('pt-2 font-body text-sm text-pretty', colorScheme.textMuted)}>
          {tier.description}
        </p>
      )}
      <div className={cn('my-4 h-px w-full', colorScheme.divider)} />
      <div className="flex flex-col items-start">
        <div className={cn('font-display text-4xl', colorScheme.text)}>
          <AnimateNumber
            format={{
              style: 'currency',
              currency: 'USD',
              maximumFractionDigits: 0,
            }}
            locales="en-US"
            suffix="/ month"
            className="flex font-bold [&_.number-section-post]:ml-1.5 [&_.number-section-post]:self-center [&_.number-section-post]:font-body [&_.number-section-post]:text-sm [&_.number-section-post]:font-medium"
          >
            {tier.price}
          </AnimateNumber>
        </div>
        <p className={cn('items-center font-body text-xs', colorScheme.textMuted)}>
          <span className="text-nowrap">Billed annually at {formatMoney(tier.price * 12)}/yr.</span>
        </p>
      </div>
      {tier.benefits && tier.benefits.length > 0 && (
        <div className="mt-6 flex grow flex-col justify-start gap-3">
          {tier.benefits.map((benefit) => (
            <div key={benefit.id} className="flex items-start">
              <DynamicIcon
                name={benefit.icon as IconName}
                className={cn('mt-1 mr-2 size-4 shrink-0', colorScheme.icon)}
              />
              <p className={cn('text-sm text-pretty', colorScheme.text)}>{benefit.benefitItem}</p>
            </div>
          ))}
        </div>
      )}
      <JoinButton
        tierData={tier}
        user={user}
        userMemberLevel={userMemberLevel}
        colorScheme={colorScheme}
        className="mt-6"
        onClick={onJoinClick}
      />
    </div>
  )
}

const defaultThemes: MembershipColorSchemeKey[] = [
  'evergreen-1',
  'evergreen-2',
  'evergreen-3',
  'evergreen-4',
]

export const DesktopTierDisplay: React.FC<{
  tiers: MembershipLevel[]
  user: User | null
  userMemberLevel: MembershipLevel | null
  onJoinClick: (chosenPlan: MembershipLevel, intent: JoinButtonIntent) => Promise<void>
}> = ({ tiers, user, userMemberLevel, onJoinClick }) => {
  const freeTierCards = tiers.filter((tier) => tier.price === 0)
  const paidTierCards = tiers.filter((tier) => tier.price > 0).sort((a, b) => a.price - b.price)

  return (
    <div className="flex flex-col gap-4">
      {paidTierCards?.length > 0 && (
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {paidTierCards.map((tier, idx) => (
            <RevealSection
              once
              key={tier.id}
              delay={0.1 * idx}
              className="relative col-span-1 flex"
            >
              <TierCard
                tier={tier}
                user={user}
                userMemberLevel={userMemberLevel}
                index={idx}
                colorScheme={MembershipColorSchemes[defaultThemes[idx % defaultThemes.length]]}
                onJoinClick={onJoinClick}
              />
            </RevealSection>
          ))}
        </div>
      )}
      {freeTierCards?.length > 0 && (
        <div className="flex flex-col gap-5">
          {freeTierCards.map((tier) => {
            const colorScheme = MembershipColorSchemes.white
            const benefitRows: NonNullable<typeof tier.benefits>[] = []

            if (tier.benefits) {
              for (let i = 0; i < tier.benefits.length; i += 2) {
                benefitRows.push(tier.benefits.slice(i, i + 2))
              }
            }

            return (
              <RevealSection
                once
                className={cn(
                  'flex w-full items-center justify-between rounded-xl p-8 shadow-lg',
                  colorScheme.bg,
                )}
                key={tier.id}
              >
                <div>
                  <h2 className={cn('font-display text-2xl font-bold', colorScheme.text)}>
                    {tier.title}
                  </h2>

                  {tier.description && (
                    <p className={cn('font-body text-sm', colorScheme.text)}>{tier.description}</p>
                  )}
                  {benefitRows.length > 0 && (
                    <div className="mt-2 flex flex-col gap-y-2">
                      {benefitRows.map((row) => (
                        <div
                          key={row[0].id}
                          className="flex flex-wrap items-center gap-x-5 gap-y-2"
                        >
                          {row.map((benefit) => (
                            <div key={benefit.id} className="flex items-center">
                              <DynamicIcon
                                name={benefit.icon as IconName}
                                className={cn('mr-1.5 size-3 shrink-0', colorScheme.icon)}
                              />
                              <p className={cn('text-xs', colorScheme.text)}>
                                {benefit.benefitItem}
                              </p>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <JoinButton
                  tierData={tier}
                  user={user}
                  userMemberLevel={userMemberLevel}
                  colorScheme={colorScheme}
                  onClick={onJoinClick}
                />
              </RevealSection>
            )
          })}
        </div>
      )}
    </div>
  )
}
